import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';
import { app } from './firebase';
import { api } from './api';

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;
const FCM_TOKEN_KEY = 'easylister.fcmToken';

function getCachedToken() {
  try { return localStorage.getItem(FCM_TOKEN_KEY); } catch { return null; }
}

function setCachedToken(token) {
  try { localStorage.setItem(FCM_TOKEN_KEY, token); } catch {}
}

function clearCachedToken() {
  try { localStorage.removeItem(FCM_TOKEN_KEY); } catch {}
}

/**
 * Request notification permission, get the FCM token, and register it with
 * the backend. Safe to call multiple times — deduplication is handled
 * server-side via $addToSet.
 */
export async function initFCM() {
  try {
    const supported = await isSupported();
    if (!supported) return null;

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return null;

    const messaging = getMessaging(app);
    const token = await getToken(messaging, { vapidKey: VAPID_KEY });
    if (!token) return null;

    // Only hit the backend when the token changes
    if (token !== getCachedToken()) {
      await api.post('/protected/fcm-token', { token });
      setCachedToken(token);
    }

    return token;
  } catch (err) {
    console.error('[FCM] initFCM error:', err);
    return null;
  }
}

/**
 * Remove the current FCM token from the backend and clear the local cache.
 * Call this on logout.
 */
export async function removeFCMToken() {
  try {
    const token = getCachedToken();
    if (!token) return;
    await api.delete('/protected/fcm-token', { data: { token } });
    clearCachedToken();
  } catch (err) {
    console.error('[FCM] removeFCMToken error:', err);
  }
}

/**
 * Listen for messages while the app is in the foreground.
 * Returns an unsubscribe function.
 */
export function onForegroundMessage(callback) {
  isSupported().then((supported) => {
    if (!supported) return;
    const messaging = getMessaging(app);
    onMessage(messaging, callback);
  });
}
